// Canonical rich-text presentation — the single source of truth for how
// rich-text content looks, shared by the read-only `RichTextRenderer` and the
// Tiptap editor across every app. No app defines its own rich-text styling; if
// a style needs to change, it changes here and both surfaces follow.

// Keep uploaded images and legacy embeds inside the container width. Legacy
// notes store fixed pixel dimensions (e.g. a 560×315 YouTube iframe) that would
// otherwise overflow a narrow pane. Applied on both the display and editing
// surfaces so they match.
export const RESPONSIVE_MEDIA =
  "[&_img]:h-auto [&_img]:max-w-full [&_iframe]:max-w-full";

// Content-heading scale. Headings are inline *content* headings (not page
// titles), but the three levels get a distinct descending rhythm so structure
// reads. Sized as child variants on the rich-text container — scoped to
// headings *inside* rich-text content only, and the one definition both the
// read-only renderer and the Tiptap editor share (Tiptap applies a single class
// to every heading level, so per-level sizing has to live on the container).
export const HEADING_SCALE = "[&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg";

// Per-element classes. Heading carries the shared, size-agnostic look; the
// per-level size comes from HEADING_SCALE on the container above. Deliberately
// no `text-transform`.
export const richTextClasses = {
  heading: "m-0 font-semibold text-foreground",
  paragraph: "m-0 text-sm text-foreground",
  strong: "font-semibold",
  emphasis: "italic",
  underline: "underline",
  strike: "line-through",
  bulletList: "my-2 list-disc pl-5",
  orderedList: "my-2 list-decimal pl-5",
  listItem: "mb-1 text-sm text-foreground [&>p]:m-0",
  link: "underline",
  // Plain semantic table family (legacy content renders as native elements).
  table: "w-full caption-bottom text-sm",
  tableRow: "border-b",
  tableHead:
    "h-12 px-4 text-left align-middle font-medium text-foreground-muted",
  tableCell: "p-4 align-middle",
} as const;

// Vertical rhythm for legacy media blocks (images, embeds, tables). Unlike text,
// which authors its spacing as empty paragraphs (see the container note below),
// Redactor-era media arrives wrapped in bare `<figure>`s with no separating
// blank paragraphs — and Tailwind's preflight zeroes the UA figure margin, so
// adjacent media (e.g. an image directly followed by a video embed) would
// otherwise sit flush against each other. Give the media blocks their own margin
// so they breathe. Display-only: the editor shows legacy media inside its own
// bordered "legacy content" card, which already carries spacing. `figure` wraps
// the media, so its margin does the work; the `img`/`iframe` rules cover bare
// (unwrapped) media and collapse harmlessly inside a figure.
export const MEDIA_BLOCK_SPACING =
  "[&_figure]:my-4 [&_img]:my-4 [&_iframe]:my-4 [&_table]:my-4";

// Read-only container. No inter-block gap (e.g. `space-y-*`) for *text*: vertical
// spacing is authored *in the content* as empty paragraphs — the convention both
// editors and the legacy portal renderer use. Paragraphs are `m-0`, so the
// read-only view reproduces authored spacing 1:1 and matches the editor. Media
// blocks are the exception (see MEDIA_BLOCK_SPACING) — they carry no authored
// spacing of their own.
export const richTextRootClass = `text-sm text-foreground ${HEADING_SCALE} ${RESPONSIVE_MEDIA} ${MEDIA_BLOCK_SPACING}`;

// Editing surface. Scroll-free; height/scroll are owned by the pane around it.
export const richTextEditorRootClass = `min-h-40 px-3 py-2 text-sm text-foreground focus:outline-none whitespace-pre-wrap break-words ${HEADING_SCALE} ${RESPONSIVE_MEDIA}`;

// Tiptap StarterKit sub-extension styling, so the editing surface renders with
// the same classes the display renderer uses. Plain class strings (no Tiptap
// import); the persist-time sanitizer strips all classes, so stored HTML stays
// presentation-agnostic.
//
// Only the canonical tag set is configured. Blockquote/code/code-block/
// horizontal-rule are disabled in the editor (the sanitizer converts them away
// on load), so they have no entry. Heading is constrained to h1–h3 — the
// sanitizer demotes h4–h6 to <strong> before the editor ever sees them, and all
// three levels share the one heading style above.
export const richTextTiptapExtensionsConfig = {
  bold: { HTMLAttributes: { class: richTextClasses.strong } },
  bulletList: { HTMLAttributes: { class: richTextClasses.bulletList } },
  heading: {
    levels: [1, 2, 3] as [1, 2, 3],
    HTMLAttributes: { class: richTextClasses.heading },
  },
  italic: { HTMLAttributes: { class: richTextClasses.emphasis } },
  link: {
    // Editing surface: clicking a link should place the caret, not navigate.
    openOnClick: false,
    HTMLAttributes: { class: richTextClasses.link },
  },
  listItem: { HTMLAttributes: { class: richTextClasses.listItem } },
  orderedList: { HTMLAttributes: { class: richTextClasses.orderedList } },
  paragraph: { HTMLAttributes: { class: richTextClasses.paragraph } },
  strike: { HTMLAttributes: { class: richTextClasses.strike } },
  underline: { HTMLAttributes: { class: richTextClasses.underline } },
};
